// noinspection ES6UnusedImports
// noinspection ES6CheckImport

import _ from 'lodash'
import Guard from 'arg-guard'
import Vue from 'vue'

import Logger from '@/logger'

import Improvement from '@/models/improvement'
/* eslint-disable-next-line no-unused-vars */
import Project from '@/models/project'

import { initialState } from './state'

const MUTATIONS = {

    resetState: 'reset-state-improvements',

    setBudget: 'set-budget',
    setCurrentProject: 'set-current-project',
    setCurrentProjectInitialisationState: 'set-current-project-initialisation-state',
    setCurrentProjectImprovements: 'set-current-project-improvements',
    setCurrentProjectLoadFailState: 'set-current-project-load-fail-state',
    setCurrentProjectName: 'set-current-project-name',
    setCurrentProjectNote: 'set-current-project-note',
    setCurrentProjectServiceOffering: 'set-current-project-service-offering',
    setCurrentProjectStatus: 'set-current-project-status',
    setFilter: 'set-filter',
    setImprovementCalculationState: 'set-improvement-calculation-state',
    setMotivation: 'set-motivation',
    setVisibleSegments: 'set-visible-segments',

    appendSavings: 'append-savings',
    clearSavings: 'clear-savings',

    selectImprovement: 'select-improvement',
    deselectImprovement: 'deselect-improvement',
    recommendImprovement: 'recommend-improvement',
    clearRecommendationFromImprovement: 'clear-recommendation-from-improvement',
}

export { MUTATIONS }

const loggingCategory = 'Improvements'

export default {

    /**
     * Resets state.
     * @param {Object} state
     */
    [MUTATIONS.resetState](state) {
        for (const item in initialState)
            Vue.set(state, item, _.cloneDeep(initialState[item]))

        Logger.logObject(state, 'State (Improvements) reset', 'Vuex')
    },

    /**
     * Appends savings data to state.
     * @param {Object} state
     * @param {Object} sourceData
     */
    [MUTATIONS.appendSavings](state, sourceData) {
        if (sourceData && sourceData.length)
            sourceData.forEach(item => state.savings.push(item))

        Logger.logObject(state, `State updated ${ MUTATIONS.appendSavings }`, loggingCategory)
    },

    /**
     * Clears all savings data in state.
     * @param {Object} state
     */
    [MUTATIONS.clearSavings](state) {
        state.savings = []
        Logger.logObject(state, `State updated ${ MUTATIONS.clearSavings }`, loggingCategory)
    },

    /**
     * Remove the mark on an improvement as recommended.
     * @param {Object} state
     * @param {number} id
     */
    [MUTATIONS.clearRecommendationFromImprovement](state, id) {
        const stateImprovement = state.currentProject.improvements.find(item => item.id === id)

        if (stateImprovement && stateImprovement.recommended) {
            stateImprovement.recommended = false
            Logger.logObject(state, `State updated ${ MUTATIONS.clearRecommendationFromImprovement }`, loggingCategory)
        }
    },

    /**
     * Remove the mark on an improvement as selected.
     * @param {Object} state
     * @param {number} id
     */
    [MUTATIONS.deselectImprovement](state, id) {
        const stateImprovement = state.currentProject.improvements.find(item => item.id === id)

        if (stateImprovement && stateImprovement.accepted) {
            stateImprovement.accepted = false
            Logger.logObject(state, `State updated ${ MUTATIONS.deselectImprovement }`, loggingCategory)
        }
    },

    /**
     * Marks an improvement as recommended.
     * @param {Object} state
     * @param {number} id
     */
    [MUTATIONS.recommendImprovement](state, id) {
        const stateImprovement = state.currentProject.improvements.find(item => item.id === id)

        if (stateImprovement && !stateImprovement.recommended) {
            stateImprovement.recommended = true
            Logger.logObject(state, `State updated ${ MUTATIONS.recommendImprovement }`, loggingCategory)
        }
    },

    /**
     * Marks an improvement as selected.
     * @param {Object} state
     * @param {number} id
     */
    [MUTATIONS.selectImprovement](state, id) {

        const getAlternatives = function(innerState, item) {
            return item.alternatives
                .map(alt => innerState.currentProject.improvements.find(imp => imp.id === alt.id))
        }

        const stateImprovement = state.currentProject.improvements.find(item => item.id === id)

        if (stateImprovement && !stateImprovement.accepted) {
            const alts = getAlternatives(state, stateImprovement)
            const acceptedAlternatives = alts.filter(imp => imp.accepted)

            stateImprovement.accepted = true
            acceptedAlternatives.forEach(alt => alt.accepted = false)

            Logger.logObject(state, `State updated ${ MUTATIONS.selectImprovement }`, loggingCategory)
        }
    },

    /**
     * Sets the budget for improvements.
     * @param {Object} state
     * @param {number} budget
     */
    [MUTATIONS.setBudget](state, budget) {
        if (budget !== state.currentProject.budget) {
            state.currentProject.budget = budget
            Logger.logObject(state, `State updated ${ MUTATIONS.setBudget }`, loggingCategory)
        }
    },

    /**
     * Sets the currentProject data as a whole.
     * @param {Object} state
     * @param {Project} value
     */
    [MUTATIONS.setCurrentProject](state, value) {
        if (value)
            state.currentProject = value
        else
            state.currentProject = null

        Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProject }`, loggingCategory)
    },

    /**
     * Sets the currentProject data.
     * @param {Object} state
     * @param sourceData
     * @param dateStamp
     * @param baseMetrics
     */
    [MUTATIONS.setCurrentProjectImprovements](state, { sourceData, dateStamp, baseMetrics = [] }) {
        if (!sourceData.length) {
            state.currentProject.initialised = false
            state.currentProject.improvements = []
        } else {
            state.currentProject.asOf = dateStamp || state.currentProject.asOf
            state.currentProject.improvements = sourceData.map(item => {
                const metrics = state.savings.filter(i => _.isEqual(i.improvementIds, [ item.id ]))[0]
                const alternatives = item.alternatives && item.alternatives.length
                    ? sourceData.filter(i => item.alternatives.includes(i.id)) : []
                const projectedMetrics = metrics ? metrics.results : []
                return new Improvement(item, { baseMetrics, projectedMetrics, alternatives })
            })
        }
        Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectImprovements }`, loggingCategory)
    },

    /**
     * Sets a flag to indicate whether the improvements state has been initialised.
     * @param {Object} state
     * @param {boolean} initialised Whether the project is initialised or not.
     */
    [MUTATIONS.setCurrentProjectInitialisationState](state, initialised) {
        if (state.currentProject.initialised !== initialised) {
            state.currentProject.initialised = initialised
            Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectInitialisationState }`, loggingCategory)
        }
    },

    /**
     * Sets or unsets a flag that says that the improvements data load has failed.
     * @param {Object} state
     * @param {boolean} failed The status to set
     */
    [MUTATIONS.setCurrentProjectLoadFailState](state, failed) {
        if (state.loadFailed !== failed) {
            state.loadFailed = failed
            Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectLoadFailState }`, loggingCategory)
        }
    },

    /**
     * Sets the current project name.
     * @param {Object} state
     * @param {string} name
     */
    [MUTATIONS.setCurrentProjectName](state, name) {
        if (state.currentProject.name !== name) {
            state.currentProject.name = name
            Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectName }`, loggingCategory)
        }
    },

    /**
     * Adds or Updates a note on the current project.
     * @param {Object} state
     * @param {{ key, value }} newNote The note data
     */
    [MUTATIONS.setCurrentProjectNote](state, newNote) {
        if (newNote && (!state.currentProject.notes || !state.currentProject.notes.filter(note => note.key === newNote.key && note.value === newNote.value).length)) {
            if (state.currentProject.notes === undefined || state.currentProject.notes === null)
                state.currentProject.notes = []

            if (state.currentProject.notes.filter(note => note.key === newNote.key).length)
                state.currentProject.notes.filter(note => note.key === newNote.key)[0].value = newNote.value !== '' ? newNote.value : null
            else if (newNote.value !== null && newNote.value !== '')
                state.currentProject.notes.push(newNote)

            Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectNote }`, loggingCategory)
        }
    },

    /**
     * Adds or Updates a ServiceOffering on the current project.
     * @param {Object} state
     * @param {{ key, value }} newServiceOffering The service data
     */
    [MUTATIONS.setCurrentProjectServiceOffering](state, newServiceOffering) {
        if (newServiceOffering && (!state.currentProject.services || !state.currentProject.services.filter(service => service.key === newServiceOffering.key && service.value === newServiceOffering.value).length)) {
            if (state.currentProject.services === undefined || state.currentProject.services === null)
                state.currentProject.services = []

            if (state.currentProject.services.find(service => service.key === newServiceOffering.key) !== undefined)
                state.currentProject.services.filter(service => service.key === newServiceOffering.key)[0].value = newServiceOffering.value !== '' ? newServiceOffering.value : null
            else if (newServiceOffering.value !== null && newServiceOffering.value !== '')
                state.currentProject.services.push(newServiceOffering)

            Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectServiceOffering }`, loggingCategory)
        }
    },

    /**
     * Updates the status of the current project.
     * @param {Object} state
     * @param {string} status The new status. Only valid statuses ('', 'planning', 'submitted', 'in-progress', 'abandoned', 'complete', 'updating') are permitted.
     */
    [MUTATIONS.setCurrentProjectStatus](state, status) {
        state.currentProject.status = status
        Logger.logObject(state, `State updated ${ MUTATIONS.setCurrentProjectNote }`, loggingCategory)
    },

    /**
     * Sets or unsets a flag that says that the improvements data load has failed.
     * @param {Object} state
     * @param {{ by, value }} filter
     */
    [MUTATIONS.setFilter](state, filter = { by: '', value: true }) {
        const filterData = !filter.by ? { by: filter, value: true } : filter
        if (filterData.value === null || filterData.value === undefined) filterData.value = true
        if (state.improvementFilters[filterData.by] !== filterData.value) {
            state.improvementFilters[filterData.by] = filterData.value
            Logger.logObject(state, `State updated ${ MUTATIONS.setFilter }`, loggingCategory)
        }
    },

    /**
     * Sets whether metrics are being loaded or not.
     * @param {Object} state
     * @param {number} calculationStatus
     */
    [MUTATIONS.setImprovementCalculationState](state, calculationStatus) {
        Guard.throwIf(calculationStatus, 'calculationStatus')
        if (calculationStatus < -1 || calculationStatus > 2)
            throw new Error('calculationStatus must be a whole number between -1 and 2')

        if (calculationStatus !== state.improvementCalculationState) {
            state.improvementCalculationState = calculationStatus
            Logger.logObject(state, `State updated ${ MUTATIONS.setImprovementCalculationState }`, loggingCategory)
        }
    },

    /**
     * Sets the motivation for the project plan.
     * @param {Object} state
     * @param {string} motivation
     */
    [MUTATIONS.setMotivation](state, motivation) {
        if (motivation !== state.currentProject.motivation) {
            state.currentProject.motivation = motivation
            Logger.logObject(state, `State updated ${ MUTATIONS.setMotivation }`, loggingCategory)
        }
    },

    /**
     * Sets the visible segments to restrict shown improvements.
     * @param {Object} state
     * @param {Array<Object>} segmentKeymap Array of segment mapping data provided from the scheme attached keymap file.
     * @param {Array<string>} visibleSegments An Array of all visible segment keys.
     */
    [MUTATIONS.setVisibleSegments](state, { segmentKeymap, visibleSegments }) {
        let updated = false
        if (!state.visibleSegments) {
            state.visibleSegments = segmentKeymap.filter(km => km.key !== 'summary').map(km => {
                return { key: km.key, visible: false }
            })
            updated = true
        }

        state.visibleSegments.forEach(segment => {
            const show = _.includes(visibleSegments, segment.key)
            if (segment.visible !== show) {
                segment.visible = show
                updated = true
            }
        })

        if (updated)
            Logger.logObject(state, `State updated ${ MUTATIONS.setVisibleSegments }`, loggingCategory)
    },
}
